import {type ReactNode, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Link} from 'react-router-dom';
import {apiClient} from '@common/http/query-client';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {useSettings} from '@common/core/settings/use-settings';
import {BuilderTemplate} from '@app/templates/builder-template';
import {ArrowRight, c, Container, Reveal} from './mrigl-shell';
import {TEMPLATE_CATEGORIES} from '@app/templates/template-categories';
import {Trans} from '@common/i18n/trans';

interface PublicTemplatesResponse extends BackendResponse {
  pagination?: {
    data: BuilderTemplate[];
    total: number;
  };
  templates?: BuilderTemplate[];
  data?:
    | BuilderTemplate[]
    | {
        data?: BuilderTemplate[];
        pagination?: {data?: BuilderTemplate[]};
        templates?: BuilderTemplate[];
      };
}

function usePublicTemplates() {
  return useQuery({
    queryKey: ['public/templates'],
    queryFn: () =>
      apiClient
        .get('public/templates', {params: {perPage: 100}})
        .then(r => r.data as PublicTemplatesResponse),
    staleTime: 5 * 60 * 1000,
  });
}

const ALL = '__all__';
const HOMEPAGE_TEMPLATE_LIMIT = 6;

interface TemplatesShowcaseProps {
  variant?: 'homepage' | 'full';
}

export function TemplatesShowcase({variant = 'homepage'}: TemplatesShowcaseProps) {
  const {data, isError, isLoading} = usePublicTemplates();
  const [category, setCategory] = useState<string>(ALL);
  const [query, setQuery] = useState('');

  const isHomepage = variant === 'homepage';
  const templates = useMemo(
    () => sortTemplatesByPremiumAndLatest(getTemplatesFromResponse(data)),
    [data],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.filter(t => {
      if (category !== ALL && t.config?.category !== category) return false;
      if (!q) return true;
      const haystack = `${t.name} ${t.config?.name ?? ''} ${
        t.config?.category ?? ''
      }`.toLowerCase();
      return haystack.includes(q);
    });
  }, [templates, category, query]);
  // only offer categories that actually contain templates
  const availableCategories = useMemo(
    () =>
      TEMPLATE_CATEGORIES.filter(cat =>
        templates.some(t => t.config?.category === cat),
      ),
    [templates],
  );
  const visibleTemplates = useMemo(
    () =>
      isHomepage ? filtered.slice(0, HOMEPAGE_TEMPLATE_LIMIT) : filtered,
    [filtered, isHomepage],
  );

  return (
    <section
      id="modeles"
      className="templates-showcase-section border-t py-80 md:py-128"
      style={{borderColor: c.rule, background: c.bg}}
      aria-labelledby="templates-title"
    >
      <Container>
        <Reveal className="mx-auto mb-40 max-w-720 text-center md:mb-56">
          <span className="eyebrow mb-20 inline-block">Modèles</span>
          <h2
            id="templates-title"
            className="display mb-16 text-3xl md:text-5xl"
            style={{lineHeight: 1.08, margin: '0 auto', maxWidth: '20ch'}}
          >
            {isHomepage ? (
              'Des modèles soignés, prêts à personnaliser.'
            ) : (
              <Trans message="All website templates" />
            )}
          </h2>
          <p
            className="mx-auto max-w-540 text-base leading-7"
            style={{color: c.inkMuted}}
          >
            {isHomepage
              ? 'Un aperçu des derniers modèles. Chaque texte, image et couleur se modifie dans l’éditeur.'
              : 'Filtrez par catégorie ou cherchez un modèle, puis ouvrez son aperçu.'}
          </p>
        </Reveal>

        {!isHomepage && (
          <Reveal className="mb-28 md:mb-36">
            <div className="templates-toolbar flex flex-wrap items-center justify-between gap-x-20 gap-y-14">
              <div
                className="templates-filters flex flex-wrap items-center gap-6"
                role="tablist"
                aria-label="Filtrer par catégorie"
              >
                <FilterChip
                  active={category === ALL}
                  onClick={() => setCategory(ALL)}
                >
                  Tous
                </FilterChip>
                {availableCategories.map(cat => (
                  <FilterChip
                    key={cat}
                    active={category === cat}
                    onClick={() => setCategory(cat)}
                  >
                    {cat}
                  </FilterChip>
                ))}
              </div>
              <SearchInput value={query} onChange={setQuery} />
            </div>
            {!isLoading && !isError && (
              <p className="templates-count m-0 mt-14 text-sm">
                {filtered.length <= 1
                  ? `${filtered.length} modèle`
                  : `${filtered.length} modèles`}
              </p>
            )}
          </Reveal>
        )}

        <Reveal>
          {isLoading ? (
            <SkeletonGrid count={isHomepage ? HOMEPAGE_TEMPLATE_LIMIT : 9} />
          ) : isError ? (
            <LoadErrorState />
          ) : filtered.length === 0 ? (
            <EmptyState
              hasQuery={query.trim().length > 0 || category !== ALL}
              onReset={() => {
                setQuery('');
                setCategory(ALL);
              }}
            />
          ) : (
            <div className="templates-grid">
              {visibleTemplates.map(t => (
                <TemplateCard key={t.id} template={t} />
              ))}
            </div>
          )}
        </Reveal>

        {isHomepage && (
          <Reveal className="mt-48 text-center">
            <Link
              to="/templates"
              className="nav-cta-primary inline-flex items-center gap-8 rounded px-24 py-12 text-base font-bold"
            >
              Voir tous les modèles
              <ArrowRight />
            </Link>
          </Reveal>
        )}
      </Container>
    </section>
  );
}

function sortTemplatesByPremiumAndLatest(templates: BuilderTemplate[]) {
  return [...templates].sort((a, b) => {
    const premiumCompare = Number(b.premium) - Number(a.premium);
    if (premiumCompare !== 0) return premiumCompare;

    const latestCompare = getTemplateTimestamp(b) - getTemplateTimestamp(a);
    if (latestCompare !== 0) return latestCompare;

    return getTemplateName(a).localeCompare(getTemplateName(b), undefined, {
      sensitivity: 'base',
    });
  });
}

function getTemplateTimestamp(template: BuilderTemplate) {
  const timestamp = Date.parse(template.updated_at || '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function getTemplateName(template: BuilderTemplate) {
  return template.config?.name || template.name || '';
}

function getTemplatesFromResponse(response?: PublicTemplatesResponse | null) {
  if (!response) return [];
  if (Array.isArray(response.templates)) return response.templates;
  if (Array.isArray(response.pagination?.data)) return response.pagination.data;
  if (Array.isArray(response.data)) return response.data;
  if (
    response.data &&
    !Array.isArray(response.data) &&
    Array.isArray(response.data.data)
  ) {
    return response.data.data;
  }
  if (
    response.data &&
    !Array.isArray(response.data) &&
    Array.isArray(response.data.templates)
  ) {
    return response.data.templates;
  }
  if (
    response.data &&
    !Array.isArray(response.data) &&
    Array.isArray(response.data.pagination?.data)
  ) {
    return response.data.pagination.data;
  }
  return [];
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="filter-chip text-sm"
      data-active={active}
    >
      {children}
    </button>
  );
}

function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label
      className="search-field inline-flex items-center gap-8 rounded border bg-white px-12 py-8"
      style={{borderColor: c.rule}}
    >
      <SearchIcon />
      <input
        type="search"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Rechercher un modèle"
        aria-label="Rechercher un modèle"
        className="m-0 border-0 bg-transparent p-0 text-sm outline-none"
        style={{minWidth: 180, color: c.ink}}
      />
      {value && (
        <button
          type="button"
          aria-label="Effacer"
          onClick={() => onChange('')}
          className="search-clear inline-flex h-18 w-18 items-center justify-center rounded-full"
        >
          <ClearIcon />
        </button>
      )}
    </label>
  );
}

function TemplateCard({template}: {template: BuilderTemplate}) {
  const {base_url} = useSettings();
  const previewUrl = `${base_url}/templates/preview/${template.name}`;
  const displayName = template.config?.name || template.name;
  const category = template.config?.category;

  return (
    <article className="template-card">
      <a
        href={previewUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="template-thumb"
        aria-label={`Aperçu du modèle ${displayName}`}
      >
        {template.thumbnail ? (
          <img src={template.thumbnail} alt="" loading="lazy" />
        ) : (
          <ThumbFallback />
        )}
        {template.premium && (
          <span className="template-status-badge">Premium</span>
        )}
        <span className="template-thumb-overlay">
          <span className="template-preview-pill">
            Aperçu
            <ArrowRight />
          </span>
        </span>
      </a>
      <div className="template-meta">
        <div className="min-w-0">
          <h3 className="template-name" title={displayName}>
            {displayName}
          </h3>
          {category && <span className="template-category">{category}</span>}
        </div>
        <Link
          to="/register"
          className="template-use"
          aria-label={`Utiliser le modèle ${displayName}`}
        >
          Utiliser
          <ArrowRight />
        </Link>
      </div>
    </article>
  );
}

function ThumbFallback() {
  return (
    <div className="template-thumb-fallback" aria-hidden="true">
      <span />
    </div>
  );
}

function SkeletonGrid({count}: {count: number}) {
  return (
    <div className="templates-grid" aria-hidden="true">
      {Array.from({length: count}).map((_, i) => (
        <div key={i} className="template-card template-card-skeleton">
          <div className="template-thumb template-thumb-skeleton" />
          <div className="template-meta">
            <div className="min-w-0" style={{flex: 1}}>
              <div className="skeleton-line skeleton-line-lg" />
              <div className="skeleton-line skeleton-line-sm" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  hasQuery,
  onReset,
}: {
  hasQuery: boolean;
  onReset: () => void;
}) {
  return (
    <div className="templates-empty">
      <p className="m-0 text-base" style={{color: c.ink, fontWeight: 600}}>
        Aucun modèle ne correspond.
      </p>
      <p
        className="m-0 mt-6 text-sm"
        style={{color: c.inkMuted, lineHeight: 1.55}}
      >
        Essayez un autre mot-clé ou affichez toutes les catégories.
      </p>
      {hasQuery && (
        <button
          type="button"
          onClick={onReset}
          className="btn-link mt-16 inline-flex items-center gap-6 text-sm font-semibold"
          style={{color: c.ink}}
        >
          Réinitialiser
        </button>
      )}
    </div>
  );
}

function LoadErrorState() {
  return (
    <div className="templates-empty">
      <p className="m-0 text-base" style={{color: c.ink, fontWeight: 600}}>
        Les modèles ne se chargent pas pour le moment.
      </p>
      <p
        className="m-0 mt-6 text-sm"
        style={{color: c.inkMuted, lineHeight: 1.55}}
      >
        Vérifiez que le dossier public/builder/templates et l'API publique des
        modèles sont bien uploadés.
      </p>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="6" cy="6" r="4.25" stroke={c.inkMuted} strokeWidth="1.5" />
      <path
        d="M9.5 9.5l2.5 2.5"
        stroke={c.inkMuted}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 2l6 6M8 2l-6 6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
